import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableSkeleton, ConfirmModal, RowActions, RowAction, ListEmptyRow, Amount } from '@redpanda/ui';
import { formatTableDate, isAdmin } from '@redpanda/utils';
import { UserContext } from '../context/UserContext';
import { deleteTimeEntry } from '../services/api';
import { useMembers, memberName } from '../stores/lookupStore';
import { notifyDataChanged } from '../utils/dataEvents';
import { PMTimeEntry } from '../types/pm';
import TimeEntryDialog from './TimeEntryDialog';

/** 工時合計（小數兩位，去掉多餘的 0） */
export const formatHours = (hours: number) => String(Math.round(hours * 100) / 100);

interface TimeEntryTableProps {
  /** undefined＝還沒抓過（顯示骨架） */
  entries?: PMTimeEntry[];
  /** 專案工時分頁不顯示專案欄 */
  showProject?: boolean;
  showMember?: boolean;
  emptyTitle: string;
  emptyHint?: string;
}

/**
 * 工時記錄表格（工時頁、專案「工時」分頁共用）
 * 自己的記錄可以編輯、刪除；管理員可以處理所有人的，並看到成本欄（工時 × 記錄當下費率）
 */
const TimeEntryTable: React.FC<TimeEntryTableProps> = ({ entries, showProject = true, showMember = false, emptyTitle, emptyHint }) => {
  const { t, i18n } = useTranslation(['translation', 'pages']);
  const { user } = useContext(UserContext)!;
  const admin = isAdmin(user?.role);
  const members = useMembers();
  const [editing, setEditing] = useState<PMTimeEntry | null>(null);
  const [deleting, setDeleting] = useState<PMTimeEntry | null>(null);

  if (!entries) return <TableSkeleton rows={5} columns={6} showHeader={true} />;

  const canModify = (entry: PMTimeEntry) => admin || entry.userId === user?.id;
  const total = entries.reduce((sum, e) => sum + e.hours, 0);
  const totalCost = entries.reduce((sum, e) => sum + e.hours * (e.costRate ?? 0), 0);
  const columns = 4 + (showProject ? 1 : 0) + (showMember ? 1 : 0) + (admin ? 1 : 0);

  const confirmDelete = async () => {
    const entry = deleting!;
    setDeleting(null);
    try {
      await deleteTimeEntry(entry.id);
      toast.success(t('common.messages.deleteSuccess'));
      notifyDataChanged('timeEntries');
      if (entry.taskId) notifyDataChanged('tasks');
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('common.messages.operationFailed'));
    }
  };

  return (
    <>
      <div className="bg-surface shadow dark:shadow-none dark:border dark:border-line overflow-hidden rounded-lg">
        <div className="overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          <Table style={{ tableLayout: 'fixed', minWidth: '800px' }}>
            <TableHeader>
              <TableRow>
                <TableHead numeric className="w-24">{t('pmTime.fields.date', { ns: 'pages' })}</TableHead>
                {showProject && <TableHead className="w-48">{t('pmTime.fields.project', { ns: 'pages' })}</TableHead>}
                <TableHead>{t('pmTime.fields.task', { ns: 'pages' })}</TableHead>
                {showMember && <TableHead className="w-32">{t('pmTime.fields.member', { ns: 'pages' })}</TableHead>}
                <TableHead numeric className="w-20">{t('pmTime.fields.hours', { ns: 'pages' })}</TableHead>
                {admin && <TableHead numeric className="w-28">{t('pmTime.fields.cost', { ns: 'pages' })}</TableHead>}
                <TableHead className="w-28">{t('common.table.columns.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.length === 0 ? (
                <ListEmptyRow colSpan={columns} filtered={false} title={emptyTitle} hint={emptyHint} />
              ) : (
                <>
                  {entries.map(entry => (
                    <TableRow key={entry.id}>
                      <TableCell numeric>{formatTableDate(entry.date, i18n.language)}</TableCell>
                      {showProject && (
                        <TableCell className="truncate">
                          <Link to={`/projects/${entry.projectId}?tab=time`} className="text-fg hover:text-accent hover:underline">
                            {entry.project?.name}
                          </Link>
                        </TableCell>
                      )}
                      <TableCell className="whitespace-normal">
                        <div className="text-fg">{entry.task?.title || <span className="text-fg-muted">{t('pmTime.noTask', { ns: 'pages' })}</span>}</div>
                        {entry.note && <div className="mt-0.5 text-[13px] text-fg-secondary line-clamp-1">{entry.note}</div>}
                      </TableCell>
                      {showMember && <TableCell className="truncate">{memberName(members, entry.userId)}</TableCell>}
                      <TableCell numeric className="font-medium">{formatHours(entry.hours)}</TableCell>
                      {admin && <TableCell numeric><Amount value={Math.round(entry.hours * (entry.costRate ?? 0))} /></TableCell>}
                      <TableCell>
                        {canModify(entry) && (
                          <RowActions>
                            <RowAction onClick={() => setEditing(entry)}>{t('common.actions.edit')}</RowAction>
                            <RowAction tone="danger" onClick={() => setDeleting(entry)}>{t('common.actions.delete')}</RowAction>
                          </RowActions>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell className="font-semibold text-fg" colSpan={columns - 2 - (admin ? 1 : 0)}>{t('pmTime.total', { ns: 'pages' })}</TableCell>
                    <TableCell numeric className="font-semibold">{formatHours(total)}</TableCell>
                    {admin && <TableCell numeric className="font-semibold"><Amount value={Math.round(totalCost)} /></TableCell>}
                    <TableCell />
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {editing && <TimeEntryDialog isOpen entry={editing} onClose={() => setEditing(null)} />}
      <ConfirmModal
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        title={t('pmTime.deleteTitle', { ns: 'pages' })}
        message={t('pmTime.deleteMessage', { ns: 'pages' })}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        t={t}
      />
    </>
  );
};

export default TimeEntryTable;
